import React, { useState } from 'react';
import { useOrganization, ORGANIZATIONS } from '../contexts/OrganizationContext';
import { useToast } from '../contexts/ToastProvider';
import { useAuth } from '../contexts/AuthContext';
import { BuildingIcon, KeyIcon, RefreshCwIcon, CheckCircleIcon, AlertCircleIcon, LinkIcon, Loader2Icon, PlusIcon, XIcon, UserPlusIcon, MailIcon, TrashIcon, UserIcon, NetworkIcon } from 'lucide-react';
import { TreeView } from '../components/TreeView';
import { MOCK_HIERARCHY } from '../schemas/mockData';
import type { HierarchyNode, HierarchyNodeType, HierarchyLabelConfig } from '../schemas/types';
export function OrganizationSetup() {
  const {
    language,
    currentOrganization,
    getHierarchyLabels,
    updateHierarchyLabels
  } = useOrganization();
  const {
    showToast
  } = useToast();
  const {
    isAdmin
  } = useAuth();
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showHierarchyModal, setShowHierarchyModal] = useState(false);
  const [showAddNodeModal, setShowAddNodeModal] = useState(false);
  const [showHierarchyLabelsModal, setShowHierarchyLabelsModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState<HierarchyNode | null>(null);
  const [parentNodeForAdd, setParentNodeForAdd] = useState<HierarchyNode | null>(null);
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set(['node-org-kmg', 'node-school-1', 'node-school-2']));
  const [newOrgForm, setNewOrgForm] = useState({
    name: '',
    spacesOrgId: '',
    defaultChatbotId: ''
  });
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: ''
  });
  const [newNodeForm, setNewNodeForm] = useState({
    name: '',
    type: 'school' as HierarchyNodeType,
    description: '',
    gradeLevels: [] as string[]
  });
  const [hierarchyLabelsForm, setHierarchyLabelsForm] = useState<HierarchyLabelConfig>(getHierarchyLabels());
  // Mock client admin users for current organization
  const [clientAdminUsers, setClientAdminUsers] = useState([{
    id: '1',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@kyivmentalguard.org',
    status: 'active' as const,
    addedAt: '2024-01-15'
  }, {
    id: '2',
    name: 'Michael Chen',
    email: 'm.chen@kyivmentalguard.org',
    status: 'invited' as const,
    addedAt: '2024-01-20'
  }]);
  const t = {
    title: language === 'en' ? 'Organization Setup' : language === 'uk' ? 'Налаштування організації' : "Configuration de l'organisation",
    subtitle: language === 'en' ? 'Configure your organization and integrations' : language === 'uk' ? 'Налаштуйте вашу організацію та інтеграції' : 'Configurez votre organisation et vos intégrations',
    hierarchyTitle: language === 'en' ? 'Organization Hierarchy' : language === 'uk' ? 'Ієрархія організації' : "Hiérarchie de l'organisation",
    hierarchySubtitle: language === 'en' ? 'Manage schools, grade bands, and role groups' : language === 'uk' ? 'Керуйте школами, віковими групами та групами ролей' : 'Gérer les écoles, les niveaux scolaires et les groupes de rôles',
    manageHierarchy: language === 'en' ? 'Manage Hierarchy' : language === 'uk' ? 'Керувати ієрархією' : 'Gérer la hiérarchie',
    addNode: language === 'en' ? 'Add Node' : language === 'uk' ? 'Додати вузол' : 'Ajouter un nœud',
    nodeName: language === 'en' ? 'Node Name' : language === 'uk' ? 'Назва вузла' : 'Nom du nœud',
    nodeType: language === 'en' ? 'Node Type' : language === 'uk' ? 'Тип вузла' : 'Type de nœud',
    school: language === 'en' ? 'School' : language === 'uk' ? 'Школа' : 'École',
    gradeBand: language === 'en' ? 'Grade Band' : language === 'uk' ? 'Вікова група' : 'Niveau scolaire',
    roleGroup: language === 'en' ? 'Role Group' : language === 'uk' ? 'Група ролей' : 'Groupe de rôles',
    cohort: language === 'en' ? 'Cohort' : language === 'uk' ? 'Когорта' : 'Cohorte',
    description: language === 'en' ? 'Description' : language === 'uk' ? 'Опис' : 'Description',
    totalNodes: language === 'en' ? 'Total Nodes' : language === 'uk' ? 'Всього вузлів' : 'Nœuds totaux',
    totalParticipants: language === 'en' ? 'Total Participants' : language === 'uk' ? 'Всього учасників' : 'Participants totaux',
    createNewOrg: language === 'en' ? 'Create New Organization' : language === 'uk' ? 'Створити нову організацію' : 'Créer une nouvelle organisation',
    createNewOrgDesc: language === 'en' ? 'Add a new client organization' : language === 'uk' ? 'Додати нову клієнтську організацію' : 'Ajouter une nouvelle organisation cliente',
    orgDetails: language === 'en' ? 'Organization Details' : language === 'uk' ? 'Деталі організації' : "Détails de l'organisation",
    orgName: language === 'en' ? 'Organization Name' : language === 'uk' ? 'Назва організації' : "Nom de l'organisation",
    spacesIntegration: language === 'en' ? 'Knowledge Spaces Integration' : language === 'uk' ? 'Інтеграція Knowledge Spaces' : 'Intégration Knowledge Spaces',
    spacesOrgId: language === 'en' ? 'Spaces Organization ID' : language === 'uk' ? 'ID організації Spaces' : "ID d'organisation Spaces",
    defaultChatbot: language === 'en' ? 'Default Chatbot ID (Optional)' : language === 'uk' ? 'ID чатбота за замовчуванням (опціонально)' : 'ID du chatbot par défaut (optionnel)',
    testConnection: language === 'en' ? 'Test Connection' : language === 'uk' ? "Тестувати з'єднання" : 'Tester la connexion',
    testing: language === 'en' ? 'Testing...' : language === 'uk' ? 'Тестування...' : 'Test en cours...',
    connectionSuccess: language === 'en' ? 'Connection successful!' : language === 'uk' ? "З'єднання успішне!" : 'Connexion réussie!',
    connectionError: language === 'en' ? 'Connection failed' : language === 'uk' ? "З'єднання не вдалося" : 'Échec de la connexion',
    apiAccess: language === 'en' ? 'API Access' : language === 'uk' ? 'Доступ до API' : 'Accès API',
    apiKey: language === 'en' ? 'API Key' : language === 'uk' ? 'API ключ' : 'Clé API',
    generateNew: language === 'en' ? 'Generate New Key' : language === 'uk' ? 'Згенерувати новий ключ' : 'Générer une nouvelle clé',
    saveConfig: language === 'en' ? 'Save Configuration' : language === 'uk' ? 'Зберегти конфігурацію' : 'Enregistrer la configuration',
    chatbotsList: language === 'en' ? 'Connected Chatbots' : language === 'uk' ? 'Підключені чатботи' : 'Chatbots connectés',
    noChatbots: language === 'en' ? 'No chatbots connected yet' : language === 'uk' ? 'Чатботи ще не підключені' : 'Aucun chatbot connecté',
    addChatbot: language === 'en' ? 'Add in Integrations' : language === 'uk' ? 'Додати в Інтеграціях' : 'Ajouter dans Intégrations',
    cancel: language === 'en' ? 'Cancel' : language === 'uk' ? 'Скасувати' : 'Annuler',
    createOrg: language === 'en' ? 'Create Organization' : language === 'uk' ? 'Створити організацію' : "Créer l'organisation",
    orgNamePlaceholder: language === 'en' ? 'e.g., University of California' : language === 'uk' ? 'напр., Київський університет' : 'ex: Université de Paris',
    spacesOrgIdPlaceholder: language === 'en' ? 'KS-ORG-XXX-XXX' : language === 'uk' ? 'KS-ORG-XXX-XXX' : 'KS-ORG-XXX-XXX',
    chatbotIdPlaceholder: language === 'en' ? 'KS-BOT-XXX-XX (Optional)' : language === 'uk' ? 'KS-BOT-XXX-XX (Опціонально)' : 'KS-BOT-XXX-XX (Optionnel)',
    setupNewClient: language === 'en' ? 'Setup New Client Organization' : language === 'uk' ? 'Налаштування нової клієнтської організації' : 'Configuration nouvelle organisation cliente',
    setupNewClientDesc: language === 'en' ? 'Enter the details for the new client organization. This will create their account and initial configuration.' : language === 'uk' ? 'Введіть деталі для нової клієнтської організації. Це створить їхній обліковий запис та початкову конфігурацію.' : 'Entrez les détails de la nouvelle organisation cliente. Cela créera leur compte et la configuration initiale.',
    required: language === 'en' ? 'Required' : language === 'uk' ? "Обов'язково" : 'Requis',
    orgCreated: language === 'en' ? 'Organization created successfully' : language === 'uk' ? 'Організацію успішно створено' : 'Organisation créée avec succès',
    clientAdminUsers: language === 'en' ? 'Client Admin Users' : language === 'uk' ? 'Користувачі Client Admin' : 'Utilisateurs Client Admin',
    clientAdminUsersDesc: language === 'en' ? 'Manage access for client administrators who can view results and integrations' : language === 'uk' ? 'Керуйте доступом для адміністраторів клієнта, які можуть переглядати результати та інтеграції' : "Gérer l'accès pour les administrateurs clients qui peuvent consulter les résultats et les intégrations",
    addClientAdmin: language === 'en' ? 'Add Client Admin' : language === 'uk' ? 'Додати Client Admin' : 'Ajouter Client Admin',
    userName: language === 'en' ? 'Full Name' : language === 'uk' ? "Повне ім'я" : 'Nom complet',
    userEmail: language === 'en' ? 'Email Address' : language === 'uk' ? 'Електронна адреса' : 'Adresse e-mail',
    status: language === 'en' ? 'Status' : language === 'uk' ? 'Статус' : 'Statut',
    active: language === 'en' ? 'Active' : language === 'uk' ? 'Активний' : 'Actif',
    invited: language === 'en' ? 'Invited' : language === 'uk' ? 'Запрошений' : 'Invité',
    addedOn: language === 'en' ? 'Added on' : language === 'uk' ? 'Додано' : 'Ajouté le',
    revokeAccess: language === 'en' ? 'Revoke Access' : language === 'uk' ? 'Відкликати доступ' : "Révoquer l'accès",
    noClientAdmins: language === 'en' ? 'No client administrators added yet' : language === 'uk' ? 'Адміністраторів клієнта ще не додано' : 'Aucun administrateur client ajouté',
    addFirstClientAdmin: language === 'en' ? 'Add your first client administrator to grant them access to results and integrations' : language === 'uk' ? 'Додайте вашого першого адміністратора клієнта, щоб надати йому доступ до результатів та інтеграцій' : 'Ajoutez votre premier administrateur client pour lui donner accès aux résultats et aux intégrations',
    sendInvite: language === 'en' ? 'Send Invite' : language === 'uk' ? 'Надіслати запрошення' : "Envoyer l'invitation",
    userNamePlaceholder: language === 'en' ? 'e.g., John Smith' : language === 'uk' ? 'напр., Іван Петренко' : 'ex: Jean Dupont',
    userEmailPlaceholder: language === 'en' ? 'john.smith@organization.com' : language === 'uk' ? 'ivan.petrenko@organization.com' : 'jean.dupont@organisation.com',
    inviteSent: language === 'en' ? 'Invitation sent successfully' : language === 'uk' ? 'Запрошення успішно надіслано' : 'Invitation envoyée avec succès',
    accessRevoked: language === 'en' ? 'Access revoked successfully' : language === 'uk' ? 'Доступ успішно відкликано' : 'Accès révoqué avec succès',
    confirmRevoke: language === 'en' ? 'Are you sure you want to revoke access for this user?' : language === 'uk' ? 'Ви впевнені, що хочете відкликати доступ для цього користувача?' : "Êtes-vous sûr de vouloir révoquer l'accès pour cet utilisateur?",
    create: language === 'en' ? 'Create Node' : language === 'uk' ? 'Створити вузол' : 'Créer un nœud',
    customHierarchyLabels: language === 'en' ? 'Custom Hierarchy Labels' : language === 'uk' ? 'Налаштування міток ієрархії' : 'Étiquettes de hiérarchie personnalisées',
    customHierarchyLabelsDesc: language === 'en' ? 'Customize the names of hierarchy levels to match your organization structure' : language === 'uk' ? 'Налаштуйте назви рівнів ієрархії відповідно до структури вашої організації' : 'Personnalisez les noms des niveaux de hiérarchie pour correspondre à la structure de votre organisation',
    subOrganizationLabel: language === 'en' ? 'Sub-Organization Label' : language === 'uk' ? 'Мітка під-організації' : 'Étiquette de sous-organisation',
    groupLabel: language === 'en' ? 'Group Label' : language === 'uk' ? 'Мітка групи' : 'Étiquette de groupe',
    subGroupLabel: language === 'en' ? 'Sub-Group Label' : language === 'uk' ? 'Мітка під-групи' : 'Étiquette de sous-groupe',
    exampleLabels: language === 'en' ? 'Example Labels' : language === 'uk' ? 'Приклади міток' : "Exemples d'étiquettes",
    k12Example: language === 'en' ? 'K-12: School → Grade → Classroom' : language === 'uk' ? 'K-12: Школа → Клас → Кабінет' : 'K-12: École → Classe → Salle',
    corporateExample: language === 'en' ? 'Corporate: Store → Department → Team' : language === 'uk' ? 'Корпоративний: Магазин → Відділ → Команда' : 'Entreprise: Magasin → Département → Équipe',
    saveLabels: language === 'en' ? 'Save Labels' : language === 'uk' ? 'Зберегти мітки' : 'Enregistrer les étiquettes',
    labelsSaved: language === 'en' ? 'Hierarchy labels updated successfully' : language === 'uk' ? 'Мітки ієрархії успішно оновлено' : 'Étiquettes de hiérarchie mises à jour avec succès',
    configureLabels: language === 'en' ? 'Configure Labels' : language === 'uk' ? 'Налаштувати мітки' : 'Configurer les étiquettes'
  };
  const handleTestConnection = async () => {
    setIsTesting(true);
    setConnectionStatus('idle');
    await new Promise(resolve => setTimeout(resolve, 1500));
    setConnectionStatus('success');
    setIsTesting(false);
    showToast(t.connectionSuccess, 'success');
  };
  const handleToggleExpand = (nodeId: string) => {
    const newExpanded = new Set(expandedNodeIds);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodeIds(newExpanded);
  };
  const handleNodeClick = (node: HierarchyNode) => {
    setSelectedNode(node);
  };
  const handleAddChild = (parentNode: HierarchyNode) => {
    setParentNodeForAdd(parentNode);
    // Determine allowed child types based on parent type
    let defaultType: HierarchyNodeType = 'cohort';
    if (parentNode.type === 'organization') {
      defaultType = 'school';
    } else if (parentNode.type === 'school') {
      defaultType = 'grade-band';
    } else if (parentNode.type === 'grade-band') {
      defaultType = 'cohort';
    }
    setNewNodeForm({
      name: '',
      type: defaultType,
      description: '',
      gradeLevels: []
    });
    setShowAddNodeModal(true);
  };
  const handleCreateNode = () => {
    if (!newNodeForm.name) {
      showToast('Please enter a node name', 'error');
      return;
    }
    showToast(`Created ${newNodeForm.type}: ${newNodeForm.name}`, 'success');
    setShowAddNodeModal(false);
    setParentNodeForAdd(null);
    setNewNodeForm({
      name: '',
      type: 'school',
      description: '',
      gradeLevels: []
    });
  };
  const getAllowedChildTypes = (parentType: HierarchyNodeType): HierarchyNodeType[] => {
    switch (parentType) {
      case 'organization':
        return ['school', 'role-group'];
      case 'school':
        return ['grade-band'];
      case 'grade-band':
        return ['cohort'];
      case 'role-group':
        return [];
      case 'cohort':
        return [];
      default:
        return [];
    }
  };
  const handleSave = () => {
    showToast(language === 'en' ? 'Configuration saved successfully' : language === 'uk' ? 'Конфігурацію успішно збережено' : 'Configuration enregistrée avec succès', 'success');
  };
  const handleGenerateKey = () => {
    showToast(language === 'en' ? 'New API key generated' : language === 'uk' ? 'Згенеровано новий API ключ' : 'Nouvelle clé API générée', 'success');
  };
  const handleCreateOrg = () => {
    if (!newOrgForm.name || !newOrgForm.spacesOrgId) {
      showToast(language === 'en' ? 'Please fill in all required fields' : language === 'uk' ? "Будь ласка, заповніть всі обов'язкові поля" : 'Veuillez remplir tous les champs requis', 'error');
      return;
    }
    // In a real app, this would make an API call to create the organization
    showToast(t.orgCreated, 'success');
    setShowCreateModal(false);
    setNewOrgForm({
      name: '',
      spacesOrgId: '',
      defaultChatbotId: ''
    });
  };
  const handleAddClientAdmin = () => {
    if (!newUserForm.name || !newUserForm.email) {
      showToast(language === 'en' ? 'Please fill in all required fields' : language === 'uk' ? "Будь ласка, заповніть всі обов'язкові поля" : 'Veuillez remplir tous les champs requis', 'error');
      return;
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUserForm.email)) {
      showToast(language === 'en' ? 'Please enter a valid email address' : language === 'uk' ? 'Будь ласка, введіть дійсну електронну адресу' : 'Veuillez entrer une adresse e-mail valide', 'error');
      return;
    }
    // In a real app, this would make an API call to send invitation
    const newUser = {
      id: Date.now().toString(),
      name: newUserForm.name,
      email: newUserForm.email,
      status: 'invited' as const,
      addedAt: new Date().toISOString().split('T')[0]
    };
    setClientAdminUsers([...clientAdminUsers, newUser]);
    showToast(t.inviteSent, 'success');
    setShowAddUserModal(false);
    setNewUserForm({
      name: '',
      email: ''
    });
  };
  const handleRevokeAccess = (userId: string, userName: string) => {
    if (window.confirm(`${t.confirmRevoke}\n\n${userName}`)) {
      setClientAdminUsers(clientAdminUsers.filter(user => user.id !== userId));
      showToast(t.accessRevoked, 'success');
    }
  };
  const handleSaveHierarchyLabels = () => {
    updateHierarchyLabels(hierarchyLabelsForm);
    showToast(t.labelsSaved, 'success');
    setShowHierarchyLabelsModal(false);
  };
  // Mock chatbots for current org
  const mockChatbots = currentOrganization.defaultChatbotId ? [{
    id: currentOrganization.defaultChatbotId,
    name: `${currentOrganization.name} Primary Bot`,
    status: 'active' as const
  }] : [];
  return <div className="p-8" data-id="element-1721">
      <div className="mb-8" data-id="element-1722">
        <div className="flex items-center justify-between" data-id="element-1723">
          <div data-id="element-1724">
            <h1 className="text-3xl font-semibold text-gray-900" data-id="element-1725">{t.title}</h1>
            <p className="text-gray-600 mt-1" data-id="element-1726">{t.subtitle}</p>
          </div>
          {isAdmin && <button onClick={() => setShowCreateModal(true)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-1727">
              <PlusIcon className="w-4 h-4" data-id="element-1728" />
              {t.createNewOrg}
            </button>}
        </div>
      </div>
      <div className="max-w-7xl space-y-6" data-id="element-1729">
        {/* Organization Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1730">
          <div className="flex items-center gap-3 mb-6" data-id="element-1731">
            <BuildingIcon className="w-6 h-6 text-blue-600" data-id="element-1732" />
            <h2 className="text-xl font-semibold text-gray-900" data-id="element-1733">
              {t.orgDetails}
            </h2>
          </div>
          <div className="space-y-4" data-id="element-1734">
            <div data-id="element-1735">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1736">
                {t.orgName}
              </label>
              <input type="text" value={currentOrganization.name} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700" data-id="element-1737" />
            </div>
          </div>
        </div>
        {/* Custom Hierarchy Labels Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1738">
          <div className="flex items-center justify-between mb-6" data-id="element-1739">
            <div className="flex items-center gap-3" data-id="element-1740">
              <NetworkIcon className="w-6 h-6 text-indigo-600" data-id="element-1741" />
              <div data-id="element-1742">
                <h2 className="text-xl font-semibold text-gray-900" data-id="element-1743">
                  {t.customHierarchyLabels}
                </h2>
                <p className="text-sm text-gray-600 mt-1" data-id="element-1744">
                  {t.customHierarchyLabelsDesc}
                </p>
              </div>
            </div>
            <button onClick={() => {
            setHierarchyLabelsForm(getHierarchyLabels());
            setShowHierarchyLabelsModal(true);
          }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-1745">
              <NetworkIcon className="w-4 h-4" data-id="element-1746" />
              {t.configureLabels}
            </button>
          </div>
          {/* Current Labels Display */}
          <div className="grid grid-cols-3 gap-4" data-id="element-1747">
            <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4" data-id="element-1748">
              <div className="text-xs font-semibold text-indigo-600 uppercase mb-2" data-id="element-1749">
                {t.subOrganizationLabel}
              </div>
              <div className="text-lg font-bold text-indigo-900" data-id="element-1750">
                {getHierarchyLabels().subOrganization}
              </div>
            </div>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4" data-id="element-1751">
              <div className="text-xs font-semibold text-blue-600 uppercase mb-2" data-id="element-1752">
                {t.groupLabel}
              </div>
              <div className="text-lg font-bold text-blue-900" data-id="element-1753">
                {getHierarchyLabels().group}
              </div>
            </div>
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4" data-id="element-1754">
              <div className="text-xs font-semibold text-purple-600 uppercase mb-2" data-id="element-1755">
                {t.subGroupLabel}
              </div>
              <div className="text-lg font-bold text-purple-900" data-id="element-1756">
                {getHierarchyLabels().subGroup}
              </div>
            </div>
          </div>
        </div>
        {/* Organization Hierarchy */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1757">
          <div className="flex items-center justify-between mb-6" data-id="element-1758">
            <div className="flex items-center gap-3" data-id="element-1759">
              <NetworkIcon className="w-6 h-6 text-purple-600" data-id="element-1760" />
              <div data-id="element-1761">
                <h2 className="text-xl font-semibold text-gray-900" data-id="element-1762">
                  {t.hierarchyTitle}
                </h2>
                <p className="text-sm text-gray-600 mt-1" data-id="element-1763">
                  {t.hierarchySubtitle}
                </p>
              </div>
            </div>
            <button onClick={() => setShowHierarchyModal(true)} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-1764">
              <NetworkIcon className="w-4 h-4" data-id="element-1765" />
              {t.manageHierarchy}
            </button>
          </div>
          {/* Hierarchy Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1766">
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4" data-id="element-1767">
              <div className="text-2xl font-bold text-purple-700" data-id="element-1768">
                {Object.keys(MOCK_HIERARCHY.flatMap).length}
              </div>
              <div className="text-sm text-purple-600 font-medium" data-id="element-1769">
                {t.totalNodes}
              </div>
            </div>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4" data-id="element-1770">
              <div className="text-2xl font-bold text-blue-700" data-id="element-1771">
                {MOCK_HIERARCHY.rootNode.metadata?.participantCount || 0}
              </div>
              <div className="text-sm text-blue-600 font-medium" data-id="element-1772">
                {t.totalParticipants}
              </div>
            </div>
          </div>
          {/* Tree Preview */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto" data-id="element-1773">
            <TreeView node={MOCK_HIERARCHY.rootNode} selectedNodeId={selectedNode?.id} expandedNodeIds={expandedNodeIds} onToggleExpand={handleToggleExpand} onNodeClick={handleNodeClick} showActions={false} data-id="element-1774" />
          </div>
        </div>
        {/* Client Admin Users Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1775">
          <div className="flex items-center justify-between mb-6" data-id="element-1776">
            <div className="flex items-center gap-3" data-id="element-1777">
              <UserIcon className="w-6 h-6 text-green-600" data-id="element-1778" />
              <div data-id="element-1779">
                <h2 className="text-xl font-semibold text-gray-900" data-id="element-1780">
                  {t.clientAdminUsers}
                </h2>
                <p className="text-sm text-gray-600 mt-1" data-id="element-1781">
                  {t.clientAdminUsersDesc}
                </p>
              </div>
            </div>
            <button onClick={() => setShowAddUserModal(true)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-1782">
              <UserPlusIcon className="w-4 h-4" data-id="element-1783" />
              {t.addClientAdmin}
            </button>
          </div>
          {clientAdminUsers.length > 0 ? <div className="space-y-3" data-id="element-1784">
              {clientAdminUsers.map(user => <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-1785">
                  <div className="flex items-center gap-4 flex-1" data-id="element-1786">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center" data-id="element-1787">
                      <UserIcon className="w-5 h-5 text-white" data-id="element-1788" />
                    </div>
                    <div className="flex-1" data-id="element-1789">
                      <div className="font-semibold text-gray-900" data-id="element-1790">
                        {user.name}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1" data-id="element-1791">
                        <MailIcon className="w-4 h-4" data-id="element-1792" />
                        {user.email}
                      </div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-1793">
                        {t.addedOn} {user.addedAt}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1794">
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`} data-id="element-1795">
                      {user.status === 'active' ? t.active : t.invited}
                    </div>
                    <button onClick={() => handleRevokeAccess(user.id, user.name)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title={t.revokeAccess} data-id="element-1796">
                      <TrashIcon className="w-4 h-4" data-id="element-1797" />
                    </button>
                  </div>
                </div>)}
            </div> : <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-id="element-1798">
              <UserPlusIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" data-id="element-1799" />
              <p className="text-gray-600 font-medium mb-2" data-id="element-1800">
                {t.noClientAdmins}
              </p>
              <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto" data-id="element-1801">
                {t.addFirstClientAdmin}
              </p>
              <button onClick={() => setShowAddUserModal(true)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium" data-id="element-1802">
                {t.addClientAdmin}
              </button>
            </div>}
        </div>
        {/* Spaces Integration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1803">
          <div className="flex items-center gap-3 mb-6" data-id="element-1804">
            <LinkIcon className="w-6 h-6 text-purple-600" data-id="element-1805" />
            <h2 className="text-xl font-semibold text-gray-900" data-id="element-1806">
              {t.spacesIntegration}
            </h2>
          </div>
          <div className="space-y-4" data-id="element-1807">
            <div data-id="element-1808">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1809">
                {t.spacesOrgId}
              </label>
              <input type="text" value={currentOrganization.spacesOrgId} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-mono" data-id="element-1810" />
            </div>
            <div data-id="element-1811">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1812">
                {t.defaultChatbot}
              </label>
              <input type="text" value={currentOrganization.defaultChatbotId || ''} placeholder="KS-BOT-XXX-XX" readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-mono" data-id="element-1813" />
            </div>
            <div className="flex gap-3" data-id="element-1814">
              <button onClick={handleTestConnection} disabled={isTesting} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50" data-id="element-1815">
                {isTesting ? <>
                    <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-1816" />
                    {t.testing}
                  </> : <>
                    <RefreshCwIcon className="w-4 h-4" data-id="element-1817" />
                    {t.testConnection}
                  </>}
              </button>
              {connectionStatus === 'success' && <div className="flex items-center gap-2 text-green-700 bg-green-50 px-4 py-2 rounded-lg border border-green-200" data-id="element-1818">
                  <CheckCircleIcon className="w-4 h-4" data-id="element-1819" />
                  <span className="text-sm font-medium" data-id="element-1820">
                    {t.connectionSuccess}
                  </span>
                </div>}
              {connectionStatus === 'error' && <div className="flex items-center gap-2 text-red-700 bg-red-50 px-4 py-2 rounded-lg border border-red-200" data-id="element-1821">
                  <AlertCircleIcon className="w-4 h-4" data-id="element-1822" />
                  <span className="text-sm font-medium" data-id="element-1823">
                    {t.connectionError}
                  </span>
                </div>}
            </div>
          </div>
        </div>
        {/* Connected Chatbots */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1824">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1825">
            {t.chatbotsList}
          </h3>
          {mockChatbots.length > 0 ? <div className="space-y-3" data-id="element-1826">
              {mockChatbots.map(bot => <div key={bot.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1827">
                  <div data-id="element-1828">
                    <div className="font-medium text-gray-900" data-id="element-1829">{bot.name}</div>
                    <div className="text-sm text-gray-600 font-mono" data-id="element-1830">
                      {bot.id}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-1831">
                    <div className="w-2 h-2 bg-green-500 rounded-full" data-id="element-1832"></div>
                    <span className="text-sm text-green-700 font-medium" data-id="element-1833">
                      Active
                    </span>
                  </div>
                </div>)}
            </div> : <div className="text-center py-8" data-id="element-1834">
              <p className="text-gray-500 mb-4" data-id="element-1835">{t.noChatbots}</p>
              <button className="text-blue-600 hover:text-blue-700 text-sm font-medium" data-id="element-1836">
                {t.addChatbot} →
              </button>
            </div>}
        </div>
        {/* API Access */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1837">
          <div className="flex items-center gap-3 mb-6" data-id="element-1838">
            <KeyIcon className="w-6 h-6 text-orange-600" data-id="element-1839" />
            <h2 className="text-xl font-semibold text-gray-900" data-id="element-1840">
              {t.apiAccess}
            </h2>
          </div>
          <div className="space-y-4" data-id="element-1841">
            <div data-id="element-1842">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1843">
                {t.apiKey}
              </label>
              <div className="flex gap-2" data-id="element-1844">
                <input type="password" value="sk_live_xxxxxxxxxxxxxxxxxxxxxxxx" readOnly className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-mono" data-id="element-1845" />
                <button onClick={handleGenerateKey} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700" data-id="element-1846">
                  {t.generateNew}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Save Button */}
        <div className="flex justify-end" data-id="element-1847">
          <button onClick={handleSave} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-1848">
            {t.saveConfig}
          </button>
        </div>
      </div>
      {/* Hierarchy Management Modal */}
      {showHierarchyModal && <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity" onClick={() => setShowHierarchyModal(false)} data-id="element-1849" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1850">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-1851">
              <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-8 py-6 flex items-center justify-between" data-id="element-1852">
                <div className="flex items-center gap-3" data-id="element-1853">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-1854">
                    <NetworkIcon className="w-6 h-6 text-white" data-id="element-1855" />
                  </div>
                  <h2 className="text-2xl font-bold text-white" data-id="element-1856">
                    {t.hierarchyTitle}
                  </h2>
                </div>
                <button onClick={() => setShowHierarchyModal(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-id="element-1857">
                  <XIcon className="w-6 h-6 text-white" data-id="element-1858" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-8" data-id="element-1859">
                <TreeView node={MOCK_HIERARCHY.rootNode} selectedNodeId={selectedNode?.id} expandedNodeIds={expandedNodeIds} onToggleExpand={handleToggleExpand} onNodeClick={handleNodeClick} onAddChild={handleAddChild} onEdit={node => {
              setSelectedNode(node);
              showToast(`Edit: ${node.name}`, 'info');
            }} onDelete={node => {
              if (window.confirm(`Delete ${node.name}?`)) {
                showToast(`Deleted: ${node.name}`, 'success');
              }
            }} showActions={true} data-id="element-1860" />
              </div>
              {/* Selected Node Details */}
              {selectedNode && <div className="border-t-2 border-gray-200 bg-gray-50 p-6" data-id="element-1861">
                  <h3 className="font-bold text-gray-900 mb-4" data-id="element-1862">
                    Selected: {selectedNode.name}
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-1863">
                    <div data-id="element-1864">
                      <span className="text-gray-600" data-id="element-1865">Type:</span>{' '}
                      <span className="font-semibold capitalize" data-id="element-1866">
                        {selectedNode.type.replace('-', ' ')}
                      </span>
                    </div>
                    {selectedNode.metadata?.participantCount !== undefined && <div data-id="element-1867">
                        <span className="text-gray-600" data-id="element-1868">Participants:</span>{' '}
                        <span className="font-semibold" data-id="element-1869">
                          {selectedNode.metadata.participantCount}
                        </span>
                      </div>}
                    {selectedNode.metadata?.description && <div className="col-span-2" data-id="element-1870">
                        <span className="text-gray-600" data-id="element-1871">Description:</span>{' '}
                        <span className="font-semibold" data-id="element-1872">
                          {selectedNode.metadata.description}
                        </span>
                      </div>}
                  </div>
                </div>}
            </div>
          </div>
        </>}
      {/* Add Node Modal */}
      {showAddNodeModal && parentNodeForAdd && <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity" onClick={() => setShowAddNodeModal(false)} data-id="element-1873" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1874">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full" data-id="element-1875">
              <div className="bg-gradient-to-r from-green-600 to-green-500 px-8 py-6 flex items-center justify-between rounded-t-2xl" data-id="element-1876">
                <div className="flex items-center gap-3" data-id="element-1877">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-1878">
                    <PlusIcon className="w-6 h-6 text-white" data-id="element-1879" />
                  </div>
                  <div data-id="element-1880">
                    <h2 className="text-2xl font-bold text-white" data-id="element-1881">
                      {t.addNode}
                    </h2>
                    <p className="text-green-100 text-sm" data-id="element-1882">
                      Parent: {parentNodeForAdd.name}
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowAddNodeModal(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-id="element-1883">
                  <XIcon className="w-6 h-6 text-white" data-id="element-1884" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-1885">
                <div data-id="element-1886">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1887">
                    {t.nodeName} <span className="text-red-500" data-id="element-1888">*</span>
                  </label>
                  <input type="text" value={newNodeForm.name} onChange={e => setNewNodeForm({
                ...newNodeForm,
                name: e.target.value
              })} placeholder="e.g., Lincoln Elementary" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500" data-id="element-1889" />
                </div>
                <div data-id="element-1890">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1891">
                    {t.nodeType} <span className="text-red-500" data-id="element-1892">*</span>
                  </label>
                  <select value={newNodeForm.type} onChange={e => setNewNodeForm({
                ...newNodeForm,
                type: e.target.value as HierarchyNodeType
              })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500" data-id="element-1893">
                    {getAllowedChildTypes(parentNodeForAdd.type).map(type => <option key={type} value={type} data-id="element-1894">
                        {type === 'school' && t.school}
                        {type === 'grade-band' && t.gradeBand}
                        {type === 'role-group' && t.roleGroup}
                        {type === 'cohort' && t.cohort}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-1895">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1896">
                    {t.description}
                  </label>
                  <textarea value={newNodeForm.description} onChange={e => setNewNodeForm({
                ...newNodeForm,
                description: e.target.value
              })} rows={3} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500" data-id="element-1897" />
                </div>
              </div>
              <div className="px-8 py-6 border-t-2 border-gray-100 bg-gray-50 rounded-b-2xl flex gap-4" data-id="element-1898">
                <button onClick={() => setShowAddNodeModal(false)} className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-100 transition-all font-semibold text-gray-700" data-id="element-1899">
                  {t.cancel}
                </button>
                <button onClick={handleCreateNode} className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-xl hover:from-green-700 hover:to-green-600 transition-all font-semibold shadow-md" data-id="element-1900">
                  {t.create}
                </button>
              </div>
            </div>
          </div>
        </>}
      {/* Create Organization Modal */}
      {showCreateModal && <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowCreateModal(false)} data-id="element-1901" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1902">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-1903">
              <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-1904">
                <div data-id="element-1905">
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-1906">
                    {t.setupNewClient}
                  </h2>
                  <p className="text-gray-600 mt-1 text-sm" data-id="element-1907">
                    {t.setupNewClientDesc}
                  </p>
                </div>
                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1908">
                  <XIcon className="w-5 h-5 text-gray-500" data-id="element-1909" />
                </button>
              </div>
              <div className="p-6 space-y-6" data-id="element-1910">
                <div data-id="element-1911">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1912">
                    {t.orgName}{' '}
                    <span className="text-red-500" data-id="element-1913">* {t.required}</span>
                  </label>
                  <input type="text" value={newOrgForm.name} onChange={e => setNewOrgForm({
                ...newOrgForm,
                name: e.target.value
              })} placeholder={t.orgNamePlaceholder} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1914" />
                </div>
                <div data-id="element-1915">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1916">
                    {t.spacesOrgId}{' '}
                    <span className="text-red-500" data-id="element-1917">* {t.required}</span>
                  </label>
                  <input type="text" value={newOrgForm.spacesOrgId} onChange={e => setNewOrgForm({
                ...newOrgForm,
                spacesOrgId: e.target.value
              })} placeholder={t.spacesOrgIdPlaceholder} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono" data-id="element-1918" />
                  <p className="text-xs text-gray-500 mt-2" data-id="element-1919">
                    This should match the organization ID in Knowledge Spaces
                  </p>
                </div>
                <div data-id="element-1920">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1921">
                    {t.defaultChatbot}
                  </label>
                  <input type="text" value={newOrgForm.defaultChatbotId} onChange={e => setNewOrgForm({
                ...newOrgForm,
                defaultChatbotId: e.target.value
              })} placeholder={t.chatbotIdPlaceholder} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono" data-id="element-1922" />
                  <p className="text-xs text-gray-500 mt-2" data-id="element-1923">
                    Optional: Can be configured later in Integrations
                  </p>
                </div>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4" data-id="element-1924">
                  <p className="text-sm text-blue-900" data-id="element-1925">
                    After creating the organization, you can configure
                    additional settings like API keys, chatbot integrations, and
                    access permissions.
                  </p>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl flex gap-3" data-id="element-1926">
                <button onClick={() => setShowCreateModal(false)} className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium text-gray-700" data-id="element-1927">
                  {t.cancel}
                </button>
                <button onClick={handleCreateOrg} className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium" data-id="element-1928">
                  {t.createOrg}
                </button>
              </div>
            </div>
          </div>
        </>}
      {/* Add Client Admin Modal */}
      {showAddUserModal && <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowAddUserModal(false)} data-id="element-1929" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1930">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" data-id="element-1931">
              <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-1932">
                <div className="flex items-center gap-3" data-id="element-1933">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-1934">
                    <UserPlusIcon className="w-5 h-5 text-green-600" data-id="element-1935" />
                  </div>
                  <div data-id="element-1936">
                    <h2 className="text-xl font-bold text-gray-900" data-id="element-1937">
                      {t.addClientAdmin}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1" data-id="element-1938">
                      {currentOrganization.name}
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowAddUserModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1939">
                  <XIcon className="w-5 h-5 text-gray-500" data-id="element-1940" />
                </button>
              </div>
              <div className="p-6 space-y-4" data-id="element-1941">
                <div data-id="element-1942">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1943">
                    {t.userName}{' '}
                    <span className="text-red-500" data-id="element-1944">* {t.required}</span>
                  </label>
                  <input type="text" value={newUserForm.name} onChange={e => setNewUserForm({
                ...newUserForm,
                name: e.target.value
              })} placeholder={t.userNamePlaceholder} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" data-id="element-1945" />
                </div>
                <div data-id="element-1946">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1947">
                    {t.userEmail}{' '}
                    <span className="text-red-500" data-id="element-1948">* {t.required}</span>
                  </label>
                  <input type="email" value={newUserForm.email} onChange={e => setNewUserForm({
                ...newUserForm,
                email: e.target.value
              })} placeholder={t.userEmailPlaceholder} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" data-id="element-1949" />
                </div>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4" data-id="element-1950">
                  <p className="text-sm text-blue-900" data-id="element-1951">
                    An invitation email will be sent to this address with
                    instructions to set up their account and access the
                    platform.
                  </p>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl flex gap-3" data-id="element-1952">
                <button onClick={() => setShowAddUserModal(false)} className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium text-gray-700" data-id="element-1953">
                  {t.cancel}
                </button>
                <button onClick={handleAddClientAdmin} className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2" data-id="element-1954">
                  <MailIcon className="w-4 h-4" data-id="element-1955" />
                  {t.sendInvite}
                </button>
              </div>
            </div>
          </div>
        </>}
      {/* Hierarchy Labels Configuration Modal */}
      {showHierarchyLabelsModal && <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity" onClick={() => setShowHierarchyLabelsModal(false)} data-id="element-1956" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1957">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full" data-id="element-1958">
              <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-8 py-6 flex items-center justify-between rounded-t-2xl" data-id="element-1959">
                <div className="flex items-center gap-3" data-id="element-1960">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-1961">
                    <NetworkIcon className="w-6 h-6 text-white" data-id="element-1962" />
                  </div>
                  <div data-id="element-1963">
                    <h2 className="text-2xl font-bold text-white" data-id="element-1964">
                      {t.customHierarchyLabels}
                    </h2>
                    <p className="text-indigo-100 text-sm" data-id="element-1965">
                      {currentOrganization.name}
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowHierarchyLabelsModal(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-id="element-1966">
                  <XIcon className="w-6 h-6 text-white" data-id="element-1967" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-1968">
                <div data-id="element-1969">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1970">
                    {t.subOrganizationLabel}
                  </label>
                  <input type="text" value={hierarchyLabelsForm.subOrganization} onChange={e => setHierarchyLabelsForm({
                ...hierarchyLabelsForm,
                subOrganization: e.target.value
              })} placeholder="e.g., School, Store, Department" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-1971" />
                </div>
                <div data-id="element-1972">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1973">
                    {t.groupLabel}
                  </label>
                  <input type="text" value={hierarchyLabelsForm.group} onChange={e => setHierarchyLabelsForm({
                ...hierarchyLabelsForm,
                group: e.target.value
              })} placeholder="e.g., Grade, Department, Team" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-1974" />
                </div>
                <div data-id="element-1975">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1976">
                    {t.subGroupLabel}
                  </label>
                  <input type="text" value={hierarchyLabelsForm.subGroup} onChange={e => setHierarchyLabelsForm({
                ...hierarchyLabelsForm,
                subGroup: e.target.value
              })} placeholder="e.g., Classroom, Job Title, Role" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-1977" />
                </div>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4" data-id="element-1978">
                  <div className="text-sm font-semibold text-blue-900 mb-2" data-id="element-1979">
                    {t.exampleLabels}
                  </div>
                  <div className="text-sm text-blue-700 space-y-1" data-id="element-1980">
                    <div data-id="element-1981">• {t.k12Example}</div>
                    <div data-id="element-1982">• {t.corporateExample}</div>
                  </div>
                </div>
              </div>
              <div className="px-8 py-6 border-t-2 border-gray-100 bg-gray-50 rounded-b-2xl flex gap-4" data-id="element-1983">
                <button onClick={() => setShowHierarchyLabelsModal(false)} className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-100 transition-all font-semibold text-gray-700" data-id="element-1984">
                  {t.cancel}
                </button>
                <button onClick={handleSaveHierarchyLabels} className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-xl hover:from-indigo-700 hover:to-indigo-600 transition-all font-semibold shadow-md" data-id="element-1985">
                  {t.saveLabels}
                </button>
              </div>
            </div>
          </div>
        </>}
    </div>;
}